import { Box } from "@mui/material";
import { v2, nums } from "../ui/v2Tokens";
import { macro } from "../ui/macroTokens";
import V2Dialog from "../ui/V2Dialog";
import { TextButton } from "../ui/primitives";
import MicronutrientList from "../ui/MicronutrientList";
import Thumb from "../ui/Thumb";
import { grams, servingLabel } from "../ui/format";
import { energyLabel, energyText, macroText, type NutritionDisplayMode } from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import { itemNutrition, pickServing, type FoodItemV2, type MacroSet } from "../model/nutritionV2Model";

type Props = {
  item: FoodItemV2;
  open: boolean;
  onClose: () => void;
};

const MACRO_CARDS = [
  { key: "calories", label: "קלוריות", color: v2.ink, soft: v2.canvas },
  { key: "protein", label: "חלבון", color: macro.protein.text, soft: macro.protein.soft },
  { key: "carbs", label: "פחמימות", color: macro.carbs.text, soft: macro.carbs.soft },
  { key: "fats", label: "שומן", color: macro.fats.text, soft: macro.fats.soft },
  { key: "fiber", label: "סיבים", color: macro.fiber.text, soft: macro.fiber.soft },
] as const;

/**
 * One figure of the dialog, in the company's unit. The serving sizes beside them are weights and
 * stay in grams, and so does fibre, which has no point value.
 */
const figure = (key: keyof MacroSet, values: MacroSet, mode: NutritionDisplayMode): string =>
  key === "calories"
    ? energyText(values.calories, mode)
    : key === "fiber"
      ? grams(values.fiber)
      : macroText(key, values[key], mode);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "9px" }}>
    <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: v2.ink3 }}>{title}</Box>
    {children}
  </Box>
);

const cell = { padding: "9px 12px", fontSize: "13px", textAlign: "start" as const, ...nums };

/**
 * Everything known about one food in the menu — the macros at the quantity the trainer
 * chose, the macros of every serving size, and the vitamins & minerals.
 */
const FoodDetailDialog = ({ item, open, onClose }: Props) => {
  const totals = itemNutrition(item);
  const serving = pickServing(item.servings, item.servingSizeId);
  const micronutrients = serving?.micronutrients || [];
  const mode = useNutritionDisplayMode();

  const source = item.isCustom
    ? { label: `קצובת ${energyLabel(mode)}`, color: v2.warn, background: v2.warnWash }
    : item.companyFoodId
      ? { label: "מזון של החברה", color: "#5B4BA8", background: "#F7F5FE" }
      : { label: "מאגר המזונות", color: v2.ink2, background: v2.canvas };

  return (
    <V2Dialog
      open={open}
      onClose={onClose}
      title={item.name}
      subtitle={[item.brand, `${item.quantity} × ${serving ? servingLabel(serving) : item.servingName}`]
        .filter(Boolean)
        .join(" · ")}
      maxWidth="620px"
      actions={<TextButton onClick={onClose}>סגור</TextButton>}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {!item.isCustom && <Thumb src={item.imageUrl} size={48} />}
          <Box
            sx={{
              fontSize: "11.5px",
              fontWeight: 500,
              padding: "3px 9px",
              borderRadius: "6px",
              color: source.color,
              background: source.background,
            }}
          >
            {source.label}
          </Box>
        </Box>

        <Section title="בכמות שבתפריט">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(3, 1fr)", md: "repeat(5, minmax(0, 1fr))" },
              // the 1px gap over a line-coloured ground IS the hairline between cells
              gap: "1px",
              background: v2.line,
              border: `1px solid ${v2.line}`,
              borderRadius: v2.r,
              overflow: "hidden",
            }}
          >
            {MACRO_CARDS.map((card) => (
              <Box
                key={card.key}
                data-testid={`detail-${card.key}`}
                sx={{ padding: "11px 12px", background: v2.surface }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "11.5px",
                    color: v2.ink3,
                    marginBottom: "4px",
                  }}
                >
                  <Box
                    sx={{ width: "6px", height: "6px", borderRadius: "2px", background: card.color, flex: "none" }}
                  />
                  {card.key === "calories" ? energyLabel(mode) : card.label}
                </Box>
                <Box sx={{ fontSize: "17px", fontWeight: 600, letterSpacing: "-.02em", color: v2.ink, ...nums }}>
                  {figure(card.key, totals, mode)}
                </Box>
              </Box>
            ))}
          </Box>
        </Section>

        {item.isCustom ? (
          <Box
            sx={{
              fontSize: "12px",
              color: v2.ink2,
              lineHeight: 1.6,
              padding: "12px",
              borderRadius: v2.rSm,
              background: v2.canvas,
              border: `1px solid ${v2.line}`,
            }}
          >
            זו קצובת {energyLabel(mode)} ולא מזון — אין לה גדלי מנה, פירוט מקרו או ערכים תזונתיים.
          </Box>
        ) : (
          <>
            <Section title="ערכים לפי גודל מנה">
              <Box
                sx={{
                  border: `1px solid ${v2.line}`,
                  borderRadius: v2.r,
                  overflowX: "auto",
                }}
              >
                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: "440px" }}>
                  <Box component="thead">
                    <Box component="tr" sx={{ background: v2.canvas }}>
                      {["גודל מנה", energyLabel(mode), "חלבון", "פחמימות", "שומן", "סיבים"].map((head) => (
                        <Box
                          key={head}
                          component="th"
                          sx={{ ...cell, fontSize: "11.5px", fontWeight: 500, color: v2.ink3, whiteSpace: "nowrap" }}
                        >
                          {head}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {item.servings.map((option) => {
                      const active = option.id === serving?.id;
                      return (
                        <Box
                          key={option.id ?? option.servingName}
                          component="tr"
                          data-testid="serving-row"
                          sx={{
                            borderTop: `1px solid ${v2.lineSoft}`,
                            background: active ? v2.brandWash : v2.surface,
                          }}
                        >
                          <Box
                            component="td"
                            sx={{ ...cell, fontWeight: active ? 600 : 500, color: v2.ink, whiteSpace: "nowrap" }}
                          >
                            {servingLabel(option)}
                            {active && (
                              <Box component="span" sx={{ fontWeight: 400, fontSize: "11px", color: v2.brandText }}>
                                {" · בתפריט"}
                              </Box>
                            )}
                          </Box>
                          <Box component="td" sx={{ ...cell, fontWeight: 500, color: v2.ink }}>
                            {figure("calories", option.nutrition, mode)}
                          </Box>
                          <Box component="td" sx={{ ...cell, color: macro.protein.text }}>
                            {figure("protein", option.nutrition, mode)}
                          </Box>
                          <Box component="td" sx={{ ...cell, color: macro.carbs.text }}>
                            {figure("carbs", option.nutrition, mode)}
                          </Box>
                          <Box component="td" sx={{ ...cell, color: macro.fats.text }}>
                            {figure("fats", option.nutrition, mode)}
                          </Box>
                          <Box component="td" sx={{ ...cell, color: macro.fiber.text }}>
                            {figure("fiber", option.nutrition, mode)}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Section>

            <Section
              title={`ויטמינים ומינרלים${serving ? ` · ל${servingLabel(serving)}` : ""}`}
            >
              <MicronutrientList micronutrients={micronutrients} />
            </Section>
          </>
        )}
      </Box>
    </V2Dialog>
  );
};

export default FoodDetailDialog;
